import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DataLabel, fmtDate, daysUntil, urgencyClass } from "@/lib/utils-ui";
import { toast } from "sonner";
import { QrCode, Plus, Search, Upload, History, Trash2, Pencil } from "lucide-react";
import ConfirmDialog from "@/components/ConfirmDialog";
import ExportMenu from "@/components/ExportMenu";
import AssetHistoryModal from "@/components/AssetHistoryModal";

const TYPES = ["laptop","desktop","server","firewall","switch","access_point","printer","nas","phone","other"];
const STATUSES = ["active","repair","scrap","lost","in_stock"];

export default function InventoryPage() {
  const { user } = useAuth();
  const [assets, setAssets] = useState([]);
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [q, setQ] = useState("");
  const [statusF, setStatusF] = useState("");
  const [typeF, setTypeF] = useState("");
  const [open, setOpen] = useState(false);
  const [qrData, setQrData] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkCompany, setBulkCompany] = useState("");
  const [bulkResult, setBulkResult] = useState(null);
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [historyAsset, setHistoryAsset] = useState(null);
  const [deleteAsset, setDeleteAsset] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ type: "laptop", serial_number: "", brand: "", model: "", location: "", status: "active", purchase_date: "", warranty_expiry: "", assigned_user_id: "", company_id: "" });

  const load = async () => {
    const params = {};
    if (q) params.q = q;
    if (statusF) params.status = statusF;
    if (typeF) params.type = typeF;
    const { data } = await api.get("/assets", { params });
    setAssets(data);
  };

  useEffect(() => {
    load();
    api.get("/auth/users").then(r => setUsers(r.data)).catch(() => {});
    api.get("/companies").then(r => setCompanies(r.data)).catch(() => {});
  }, []); // eslint-disable-line

  useEffect(() => { load(); }, [q, statusF, typeF]); // eslint-disable-line

  const createAsset = async () => {
    try {
      const payload = { ...form };
      if (!payload.assigned_user_id) delete payload.assigned_user_id;
      if (user.role === "super_admin" && !editing && !payload.company_id) {
        toast.error("Select a company"); return;
      }
      if (user.role !== "super_admin") delete payload.company_id;
      if (editing) {
        await api.put(`/assets/${editing.id}`, payload);
        toast.success("Asset updated");
      } else {
        await api.post("/assets", payload);
        toast.success("Asset created");
      }
      setOpen(false); setEditing(null);
      setForm({ type: "laptop", serial_number: "", brand: "", model: "", location: "", status: "active", purchase_date: "", warranty_expiry: "", assigned_user_id: "", company_id: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const startEdit = (a) => {
    setEditing(a);
    setForm({
      type: a.type, serial_number: a.serial_number, brand: a.brand || "", model: a.model || "",
      location: a.location || "", status: a.status,
      purchase_date: a.purchase_date || "", warranty_expiry: a.warranty_expiry || "",
      assigned_user_id: a.assigned_user_id || "", company_id: a.company_id,
    });
    setOpen(true);
  };

  const showQr = async (id) => {
    const { data } = await api.get(`/assets/${id}/qr`);
    setQrData(data);
  };

  const confirmDelete = async () => {
    if (!deleteAsset) return;
    try {
      await api.delete(`/assets/${deleteAsset.id}`);
      toast.success("Asset deleted (soft)");
      setDeleteAsset(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const bulkUpload = async () => {
    if (!bulkFile) { toast.error("Select an .xlsx file"); return; }
    if (user.role === "super_admin" && !bulkCompany) { toast.error("Select company"); return; }
    setBulkLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", bulkFile);
      const q = user.role === "super_admin" ? `?company_id=${bulkCompany}` : "";
      const { data } = await api.post(`/assets/bulk-upload${q}`, fd, { headers: { "Content-Type": "multipart/form-data" }});
      setBulkResult(data);
      if (data.imported > 0) toast.success(`Imported ${data.imported} assets`);
      if (data.errors?.length) toast.warning(`${data.errors.length} row(s) skipped`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk upload failed");
    } finally { setBulkLoading(false); }
  };

  const userName = (id) => users.find(u => u.id === id)?.full_name || "—";
  const canEdit = ["super_admin","company_admin","it_admin"].includes(user?.role);

  return (
    <div className="p-8 space-y-6" data-testid="inventory-page">
      <div className="flex items-start justify-between">
        <div>
          <DataLabel>Module 01</DataLabel>
          <h1 className="font-head font-black uppercase text-4xl tracking-tighter mt-1">Inventory</h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-xl">All hardware across the estate. {assets.length} records.</p>
        </div>
        {canEdit && (
          <div className="flex items-center gap-2">
            <ExportMenu module="assets" />
            <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
              <DialogTrigger asChild>
                <Button data-testid="bulk-upload-btn" variant="outline" className="h-10 uppercase tracking-wider text-xs font-head border-black hover:bg-black hover:text-white">
                  <Upload size={14} className="mr-1"/>Bulk Import
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader><DialogTitle className="font-head uppercase tracking-tight">Bulk Import Assets</DialogTitle></DialogHeader>
                <div className="space-y-3 text-xs">
                  <div className="border border-border bg-muted p-3 space-y-1">
                    <div className="data-label">Expected columns (.xlsx)</div>
                    <div className="font-mono text-[11px]">type · serial_number · brand · model · location · status · purchase_date · warranty_expiry · assigned_user_email · notes</div>
                    <div className="text-muted-foreground pt-1">Only <span className="font-mono">type</span> and <span className="font-mono">serial_number</span> are required. Dates in YYYY-MM-DD.</div>
                  </div>
                  {user.role === "super_admin" && (
                    <div><Label className="data-label">Company</Label>
                      <Select value={bulkCompany} onValueChange={setBulkCompany}>
                        <SelectTrigger data-testid="bulk-company"><SelectValue placeholder="Select"/></SelectTrigger>
                        <SelectContent>{companies.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  )}
                  <div><Label className="data-label">File (.xlsx)</Label>
                    <Input data-testid="bulk-file-input" type="file" accept=".xlsx,.xlsm" onChange={e => setBulkFile(e.target.files?.[0] || null)}/></div>
                  {bulkResult && (
                    <div className="border border-border p-3" data-testid="bulk-result">
                      <div className="font-semibold">Imported: <span className="font-mono text-[#0a7a3f]">{bulkResult.imported}</span> · Processed: {bulkResult.rows_processed}</div>
                      {bulkResult.errors?.length > 0 && (
                        <div className="mt-2">
                          <div className="data-label">Errors ({bulkResult.errors.length})</div>
                          <ul className="mt-1 font-mono text-[10px] max-h-32 overflow-y-auto space-y-0.5 text-muted-foreground">
                            {bulkResult.errors.slice(0,20).map((e,i) => <li key={i}>· {e}</li>)}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <DialogFooter>
                  <Button data-testid="bulk-submit-btn" onClick={bulkUpload} disabled={bulkLoading} className="bg-black text-white hover:bg-[#FF5000]">
                    {bulkLoading ? "Importing…" : "Import"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
              <DialogTrigger asChild>
                <Button data-testid="new-asset-btn" onClick={() => { setEditing(null); setForm({ type: "laptop", serial_number: "", brand: "", model: "", location: "", status: "active", purchase_date: "", warranty_expiry: "", assigned_user_id: "", company_id: "" }); }} className="bg-black text-white hover:bg-[#FF5000] h-10 uppercase tracking-wider text-xs font-head">
                  <Plus size={14} className="mr-1"/> New Asset
                </Button>
              </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle className="font-head uppercase tracking-tight">{editing ? "Edit Asset" : "Register Asset"}</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                {user.role === "super_admin" && (
                  <div className="col-span-2">
                    <Label className="data-label">Company</Label>
                    <Select value={form.company_id} onValueChange={(v) => setForm({...form, company_id: v})}>
                      <SelectTrigger data-testid="asset-form-company"><SelectValue placeholder="Select company"/></SelectTrigger>
                      <SelectContent>{companies.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
                <div><Label className="data-label">Type</Label>
                  <Select value={form.type} onValueChange={(v) => setForm({...form, type: v})}>
                    <SelectTrigger data-testid="asset-form-type"><SelectValue/></SelectTrigger>
                    <SelectContent>{TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="data-label">Status</Label>
                  <Select value={form.status} onValueChange={(v) => setForm({...form, status: v})}>
                    <SelectTrigger><SelectValue/></SelectTrigger>
                    <SelectContent>{STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="col-span-2"><Label className="data-label">Serial Number</Label><Input data-testid="asset-form-serial" className="font-mono" value={form.serial_number} onChange={e => setForm({...form, serial_number: e.target.value})}/></div>
                <div><Label className="data-label">Brand</Label><Input value={form.brand} onChange={e => setForm({...form, brand: e.target.value})}/></div>
                <div><Label className="data-label">Model</Label><Input value={form.model} onChange={e => setForm({...form, model: e.target.value})}/></div>
                <div className="col-span-2"><Label className="data-label">Location</Label><Input value={form.location} onChange={e => setForm({...form, location: e.target.value})}/></div>
                <div><Label className="data-label">Purchase Date</Label><Input type="date" value={form.purchase_date} onChange={e => setForm({...form, purchase_date: e.target.value})}/></div>
                <div><Label className="data-label">Warranty Expiry</Label><Input type="date" value={form.warranty_expiry} onChange={e => setForm({...form, warranty_expiry: e.target.value})}/></div>
                <div className="col-span-2"><Label className="data-label">Assign To</Label>
                  <Select value={form.assigned_user_id} onValueChange={(v) => setForm({...form, assigned_user_id: v})}>
                    <SelectTrigger><SelectValue placeholder="Unassigned"/></SelectTrigger>
                    <SelectContent>{users.map(u => <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button data-testid="asset-form-submit" onClick={createAsset} className="bg-black text-white hover:bg-[#FF5000]">{editing ? "Save changes" : "Create"}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 border border-border bg-white p-3" data-testid="filters">
        <Search size={14} className="text-muted-foreground"/>
        <Input data-testid="asset-search" placeholder="Search serial, brand, model…" value={q} onChange={e => setQ(e.target.value)} className="border-0 shadow-none font-mono max-w-xs" />
        <Select value={typeF || "_all"} onValueChange={(v) => setTypeF(v === "_all" ? "" : v)}>
          <SelectTrigger data-testid="filter-type" className="w-40"><SelectValue placeholder="Type"/></SelectTrigger>
          <SelectContent><SelectItem value="_all">All types</SelectItem>{TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={statusF || "_all"} onValueChange={(v) => setStatusF(v === "_all" ? "" : v)}>
          <SelectTrigger data-testid="filter-status" className="w-40"><SelectValue placeholder="Status"/></SelectTrigger>
          <SelectContent><SelectItem value="_all">All statuses</SelectItem>{STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="border border-border bg-white overflow-x-auto" data-testid="asset-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-muted">
              {["ASSET ID","TYPE","SERIAL","BRAND / MODEL","ASSIGNED","LOCATION","WARRANTY","STATUS","ACTIONS"].map(h => (
                <th key={h} className="text-left p-3 data-label">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {assets.map(a => {
              const wd = daysUntil(a.warranty_expiry);
              return (
                <tr key={a.id} className="border-b border-border hover-row" data-testid={`asset-row-${a.id}`}>
                  <td className="p-3 font-mono text-xs">{a.asset_id}</td>
                  <td className="p-3 uppercase text-xs">{a.type}</td>
                  <td className="p-3 font-mono text-xs">{a.serial_number}</td>
                  <td className="p-3">{a.brand} <span className="text-muted-foreground">{a.model}</span></td>
                  <td className="p-3">{userName(a.assigned_user_id)}</td>
                  <td className="p-3 text-xs">{a.location || "—"}</td>
                  <td className="p-3 font-mono text-xs">
                    {a.warranty_expiry ? (
                      <span className={urgencyClass(wd)}>{fmtDate(a.warranty_expiry)}</span>
                    ) : "—"}
                  </td>
                  <td className="p-3"><span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-[0.12em] font-semibold ${a.status === "active" ? "bg-[#E8F4EC] text-[#0a7a3f]" : a.status === "repair" ? "bg-[#FFF4E0] text-[#8a5a00]" : "bg-muted"}`}>{a.status}</span></td>
                  <td className="p-3">
                    <div className="flex items-center gap-3">
                      <button data-testid={`history-btn-${a.id}`} onClick={() => setHistoryAsset(a)} title="Lifecycle history" className="hover:text-[#FF5000]"><History size={14}/></button>
                      <button data-testid={`qr-btn-${a.id}`} onClick={() => showQr(a.id)} title="QR" className="hover:text-[#FF5000]"><QrCode size={14}/></button>
                      {canEdit && <button data-testid={`edit-asset-${a.id}`} onClick={() => startEdit(a)} title="Edit" className="hover:text-[#FF5000]"><Pencil size={14}/></button>}
                      {canEdit && <button data-testid={`delete-btn-${a.id}`} onClick={() => setDeleteAsset(a)} title="Delete" className="hover:text-[#FF3B30]"><Trash2 size={14}/></button>}
                    </div>
                  </td>
                </tr>
              );
            })}
            {assets.length === 0 && <tr><td colSpan={9} className="p-10 text-center text-muted-foreground">No assets.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={!!qrData} onOpenChange={() => setQrData(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-head uppercase tracking-tight">QR · {qrData?.asset_id}</DialogTitle></DialogHeader>
          {qrData && <img src={qrData.png_base64} alt="QR" className="w-full border border-border" data-testid="qr-image"/>}
          <div className="font-mono text-[10px] text-muted-foreground break-all mt-2">{qrData?.payload}</div>
        </DialogContent>
      </Dialog>

      <AssetHistoryModal asset={historyAsset} open={!!historyAsset} onOpenChange={(v) => !v && setHistoryAsset(null)} users={users} onChanged={load}/>

      <ConfirmDialog
        open={!!deleteAsset}
        onOpenChange={(v) => !v && setDeleteAsset(null)}
        title="Delete asset?"
        description={deleteAsset ? `${deleteAsset.asset_id} · ${deleteAsset.brand} ${deleteAsset.model}. The record is soft-deleted (kept in DB for audit trail). Continue?` : ""}
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={confirmDelete}
        testid="delete-asset"
      />
    </div>
  );
}
